import React from 'react';
import { LLink as Link } from '../components/LocalizedLink';
import {
  ArrowRight, Check, Home, HeartPulse, Building2,
  Languages, ShieldCheck, FileSearch, Users, Zap,
  Star, ClipboardList, HelpCircle, FileCheck2,
} from 'lucide-react';
import { reviews } from '../mock';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import Seo from '../components/Seo';
import useContent from '../hooks/useContent';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';

const stepIcons = [ClipboardList, HelpCircle, FileCheck2, Check];

export default function HomePage() {
  const c = useContent();
  const { t } = useI18n();
  const T = (k) => t(k, UI[k]);

  const heroFeats = [
    c.hero_feature_1, c.hero_feature_2, c.hero_feature_3, c.hero_feature_4, c.hero_feature_5,
  ].filter(Boolean);
  const features = heroFeats.length ? heroFeats : [
    T('features.holiday_homes'),
    T('features.permanent_homes'),
    T('features.rental_properties'),
    T('features.apartments'),
    T('features.villas'),
  ];

  const products = [
    {
      id: 'home', icon: Home,
      title: T('product.home.title'),
      description: T('product.home.description'),
      cta: T('product.home.cta'),
      href: '/home-insurance', available: true,
    },
    {
      id: 'health', icon: HeartPulse,
      title: T('product.health.title'),
      description: T('product.health.description'),
      cta: T('product.health.cta'),
      href: '/health-insurance', available: true,
    },
    {
      id: 'community', icon: Building2,
      title: T('product.community.title'),
      description: T('product.community.description'),
      cta: T('product.community.cta'),
      href: '#', available: false,
    },
  ];

  const whyChoose = [
    { icon: Languages,   title: T('why.english_title'),      text: T('why.english_text') },
    { icon: Languages,   title: T('why.swedish_title'),      text: T('why.swedish_text') },
    { icon: Languages,   title: T('why.german_title'),       text: T('why.german_text') },
    { icon: Languages,   title: T('why.spanish_title'),      text: T('why.spanish_text') },
    { icon: ShieldCheck, title: T('why.trusted_title'),      text: T('why.trusted_text') },
    { icon: FileSearch,  title: T('why.compare_title'),      text: T('why.compare_text') },
    { icon: Users,       title: T('why.specialists_title'),  text: T('why.specialists_text') },
    { icon: Zap,         title: T('why.fast_title'),         text: T('why.fast_text') },
  ];

  const steps = [
    { step: 1, title: T('how.step1_title'), text: T('how.step1_text') },
    { step: 2, title: T('how.step2_title'), text: T('how.step2_text') },
    { step: 3, title: T('how.step3_title'), text: T('how.step3_text') },
    { step: 4, title: T('how.step4_title'), text: T('how.step4_text') },
  ];

  const faqItems = [1, 2, 3, 4, 5].map((n) => ({
    q: T(`faqitem.${n}.q`), a: T(`faqitem.${n}.a`),
  }));

  return (
    <div>
      <Seo pageKey="home" />
      {/* HERO */}
      <section className="hero-bg">
        <div className="container-x pt-14 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <span className="eyebrow mb-5">{c.hero_eyebrow}</span>
            <h1 className="mt-4 text-[2.4rem] sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#0b1f3a]">
              {c.hero_title}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">{c.hero_subtitle}</p>
            <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-6 max-w-lg">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-slate-700">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                    <Check size={13} />
                  </span>
                  <span className="text-sm font-medium">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/home-insurance" className="btn-primary">
                {c.hero_cta_label || T('cta.get_quote')} <ArrowRight size={18} />
              </Link>
              <a href="#how-it-works" className="btn-outline">{T('cta.how_it_works')}</a>
            </div>
          </div>

          {c.hero_image ? (
            <div className="relative fade-up">
              <div className="rounded-3xl overflow-hidden shadow-xl bg-white">
                <img
                  src={c.hero_image}
                  alt="EasySpanishInsurance"
                  className="w-full h-[440px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-6 left-6 bg-white shadow-lg rounded-xl px-4 py-3 flex items-center gap-3 border border-slate-100">
                <span className="w-9 h-9 rounded-lg bg-[#eef4fb] text-[#0d3a8a] inline-flex items-center justify-center">
                  <ShieldCheck size={18} />
                </span>
                <div>
                  <p className="text-[13px] font-semibold text-[#0b1f3a]">{T('home.policy_card_title')}</p>
                  <p className="text-[12px] text-slate-500">{T('home.policy_card_sub')}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* PRODUCT BOXES */}
      <section className="py-20">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">{c.products_eyebrow}</span>
            <h2 className="section-title mt-3">{c.products_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((p) => {
              const Icon = p.icon;
              const disabled = !p.available;
              const cardBody = (
                <div
                  className={`card-lift bg-white border border-slate-200 rounded-2xl p-7 h-full flex flex-col ${
                    disabled ? 'opacity-70' : ''
                  }`}
                >
                  <span className="icon-tile mb-5"><Icon size={22} /></span>
                  <h3 className="text-xl font-semibold text-[#0b1f3a]">{p.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-1">{p.description}</p>
                  <span
                    className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${
                      disabled ? 'text-slate-400' : 'text-[#0d3a8a]'
                    }`}
                  >
                    {p.cta} {!disabled && <ArrowRight size={16} />}
                  </span>
                </div>
              );
              return disabled ? (
                <div key={p.id}>{cardBody}</div>
              ) : (
                <Link key={p.id} to={p.href}>{cardBody}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-[#f7fafd]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">{c.why_eyebrow}</span>
            <h2 className="section-title mt-3">{c.why_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyChoose.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="bg-white border border-slate-200 rounded-2xl p-6 card-lift">
                  <span className="icon-tile mb-4"><Icon size={20} /></span>
                  <h4 className="font-semibold text-[#0b1f3a]">{w.title}</h4>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">{c.how_eyebrow}</span>
            <h2 className="section-title mt-3">{c.how_title}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            {steps.map((s, i) => {
              const Icon = stepIcons[i] || Check;
              return (
                <div key={s.step} className="bg-white border border-slate-200 rounded-2xl p-6 relative card-lift">
                  <div className="flex items-center justify-between">
                    <span className="w-10 h-10 rounded-xl bg-[#0d3a8a] text-white inline-flex items-center justify-center font-bold">
                      {s.step}
                    </span>
                    <span className="icon-tile !w-10 !h-10"><Icon size={18} /></span>
                  </div>
                  <h4 className="mt-5 font-semibold text-[#0b1f3a]">{s.title}</h4>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.text}</p>
                  {i < steps.length - 1 && (
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center text-slate-300">
                      <ArrowRight size={18} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-center mt-10">
            <Link to="/home-insurance" className="btn-primary">{T('cta.start_quote')} <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-20 bg-[#f7fafd]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">{c.reviews_eyebrow}</span>
            <h2 className="section-title mt-3">{c.reviews_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.map((r) => (
              <div key={r.name} className="bg-white border border-slate-200 rounded-2xl p-6 card-lift">
                <div className="flex items-center gap-1 text-amber-400 mb-3">
                  {[0,1,2,3,4].map((i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed italic">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-4">
                  <p className="font-semibold text-[#0b1f3a] text-sm">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div>
            <span className="eyebrow">{c.faq_eyebrow}</span>
            <h2 className="section-title mt-3">{c.faq_title}</h2>
            <p className="text-slate-600 mt-4">{T('home.faq_hero_text')}</p>
            <Link to="/faq" className="btn-outline mt-6">{T('cta.view_all_questions')} <ArrowRight size={16} /></Link>
          </div>
          <div className="lg:col-span-2">
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={'q' + i}
                  className="border border-slate-200 rounded-xl px-5 bg-white"
                >
                  <AccordionTrigger className="text-left text-[#0b1f3a] font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-sm leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0d3a8a]">
        <div className="container-x flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white text-2xl md:text-3xl font-bold">{c.cta_title || T('home.cta_title')}</h3>
            <p className="text-slate-200 mt-2">{c.cta_subtitle || T('home.cta_sub')}</p>
          </div>
          <Link to="/home-insurance" className="inline-flex items-center gap-2 bg-white text-[#0d3a8a] font-semibold px-6 py-3 rounded-full hover:bg-slate-100 transition-colors">
            {c.cta_button || T('cta.get_quote')} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
